import { logger } from '../utils/logger';
import { auth } from '../config/firebase';

// Helper function to get auth headers with Firebase ID token
const getAuthHeaders = async (): Promise<HeadersInit> => {
    const headers: HeadersInit = {
        'Content-Type': 'application/json'
    };

    try {
        const currentUser = auth.currentUser;
        if (currentUser) {
            const idToken = await currentUser.getIdToken();
            headers['Authorization'] = `Bearer ${idToken}`;
        }
    } catch (error) {
        logger.error('[Stripe] Error getting auth token:', error);
    }

    return headers;
};

// Helper function to build API URL
const buildApiUrl = (endpoint: string): string => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3002';
    // Remove trailing slash from backendUrl
    const baseUrl = backendUrl.replace(/\/+$/, '');
    // Remove leading slash from endpoint
    const cleanEndpoint = endpoint.replace(/^\/+/, '');
    // Check if baseUrl already contains /api
    const finalUrl = baseUrl.endsWith('/api') 
        ? `${baseUrl}/${cleanEndpoint}`
        : `${baseUrl}/api/${cleanEndpoint}`;
    return finalUrl;
};

export interface CreateCheckoutSessionParams {
    planId: string;
    customerEmail: string;
    successUrl: string;
    cancelUrl: string;
    metadata?: Record<string, string>;
}

export interface CheckoutSessionResponse {
    success: boolean;
    sessionId?: string;
    url?: string;
    error?: string;
}

// Create Stripe checkout session
export const createCheckoutSession = async (
    params: CreateCheckoutSessionParams
): Promise<CheckoutSessionResponse> => {
    const apiUrl = buildApiUrl('/stripe/create-checkout-session');
    // SECURITY: Only log non-sensitive data
    logger.log('[Stripe] Creating checkout session...', { planId: params.planId });

    try {
        const headers = await getAuthHeaders();
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify(params)
        });

        const data = await response.json();

        if (!response.ok) {
            logger.error('[Stripe] Checkout session creation failed', { status: response.status });
            return {
                success: false,
                error: data.error || 'Failed to create checkout session'
            };
        }

        logger.log('[Stripe] Checkout session created successfully', { sessionId: data.sessionId });

        return data;
    } catch (error: any) {
        logger.error('[Stripe] Error creating checkout session', { message: error.message });
        return {
            success: false,
            error: error.message || 'Network error occurred'
        };
    }
};

export interface CheckoutSessionStatus {
    success: boolean;
    session?: {
        id: string;
        status: string;
        paymentStatus: string;
        customerEmail: string | null;
        amountTotal: number | null;
        currency: string | null;
        subscription: string | null;
    };
    error?: string;
}

// Verify checkout session status
export const verifyCheckoutSession = async (sessionId: string): Promise<CheckoutSessionStatus> => {
    const apiUrl = buildApiUrl(`/stripe/checkout-session/${sessionId}`);
    logger.log('[Stripe] Verifying checkout session...', { sessionId });

    try {
        const headers = await getAuthHeaders();
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers
        });

        const data = await response.json();

        if (!response.ok) {
            logger.error('[Stripe] Checkout session verification failed', { status: response.status });
            return {
                success: false,
                error: data.error || 'Failed to verify checkout session'
            };
        }

        logger.log('[Stripe] Checkout session verified successfully', {
            sessionId: data.session?.id,
            paymentStatus: data.session?.paymentStatus
        });

        return data;
    } catch (error: any) {
        logger.error('[Stripe] Error verifying checkout session', { message: error.message });
        return {
            success: false,
            error: error.message || 'Network error occurred'
        };
    }
};

// Sync subscription status from Stripe
export interface SyncSubscriptionStatusResponse {
    success: boolean;
    subscription?: {
        id: string;
        status: string;
        planId?: string | null;
    };
    error?: string;
}

// Get subscription details from Stripe
export interface SubscriptionDetailsResponse {
    success: boolean;
    subscription?: {
        id: string;
        status: string;
        planId: string | null;
        currentPeriodEnd?: number;
    };
    error?: string;
}

export const getSubscriptionDetails = async (subscriptionId: string): Promise<SubscriptionDetailsResponse> => {
    const apiUrl = buildApiUrl(`/stripe/subscription/${subscriptionId}`);
    logger.log('[Stripe] Getting subscription details...', { subscriptionId });

    try {
        const headers = await getAuthHeaders();
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers
        });

        const data = await response.json();

        if (!response.ok) {
            logger.error('[Stripe] Failed to get subscription details', { status: response.status });
            return {
                success: false,
                error: data.error || 'Failed to get subscription details'
            };
        }

        logger.log('[Stripe] Subscription details retrieved', { 
            subscriptionId: data.subscription?.id,
            status: data.subscription?.status 
        });

        return data;
    } catch (error: any) {
        logger.error('[Stripe] Error getting subscription details', { message: error.message });
        return {
            success: false,
            error: error.message || 'Network error occurred'
        };
    }
};

export const syncSubscriptionStatus = async (subscriptionId: string): Promise<SyncSubscriptionStatusResponse> => {
    const apiUrl = buildApiUrl('/stripe/sync-subscription-status');
    logger.log('[Stripe] Syncing subscription status...', { subscriptionId });

    try {
        const headers = await getAuthHeaders();
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({ subscriptionId })
        });

        const data = await response.json();

        if (!response.ok) {
            logger.error('[Stripe] Subscription status sync failed', { status: response.status });
            return {
                success: false,
                error: data.error || 'Failed to sync subscription status'
            };
        }

        logger.log('[Stripe] Subscription status synced successfully', { 
            subscriptionId: data.subscription?.id,
            status: data.subscription?.status 
        });
        return data;
    } catch (error: any) {
        logger.error('[Stripe] Error syncing subscription status', { message: error.message });
        return {
            success: false,
            error: error.message || 'Network error occurred'
        };
    }
};

// Cancel subscription response interface
export interface CancelSubscriptionResponse {
    success: boolean;
    subscription?: {
        id: string;
        status: string;
    };
    error?: string;
}

// Cancel Stripe subscription
export const cancelSubscription = async (subscriptionId: string): Promise<CancelSubscriptionResponse> => {
    const apiUrl = buildApiUrl('/stripe/cancel-subscription');
    logger.log('[Stripe] Cancelling subscription...', { subscriptionId });

    try {
        const headers = await getAuthHeaders();
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({ subscriptionId })
        });

        const data = await response.json();

        if (!response.ok) {
            logger.error('[Stripe] Subscription cancellation failed', { status: response.status });
            return {
                success: false,
                error: data.error || 'Failed to cancel subscription'
            };
        }

        logger.log('[Stripe] Subscription cancelled successfully', { 
            subscriptionId: data.subscription?.id,
            status: data.subscription?.status 
        });
        return data;
    } catch (error: any) {
        logger.error('[Stripe] Error cancelling subscription', { message: error.message });
        return {
            success: false,
            error: error.message || 'Network error occurred'
        };
    }
};

